// The price part of the hub's Filters sheet (owner, concept "30 · Filters in
// one sheet"): a chart of what the current results cost, and a min / max
// control under it. Everything is custom: no <input type="range">, no number
// field (owner rule: no browser-native pickers).
//
// Prices here are dirhams, the unit the search API filters in. Only the labels
// are converted, through useDisplayPrice, so a guest browsing in euros reads
// euros while the URL keeps the same `min_price` / `max_price` it always had.
import { useCallback, useRef, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { PW } from "@majourneys/booking";
import { Icon } from "@majourneys/icons";
import { useDisplayPrice } from "../../../lib/currency";

/** The search API's `priceHistogram` (apps/api `_shared/price-histogram.ts`). */
export interface PriceHistogram {
  /** Width of one bucket, in dirhams. */
  bucketSize: number;
  /** `buckets[i]` counts prices in [i * bucketSize, (i + 1) * bucketSize); the last one is open-ended. */
  buckets: number[];
  /** Start of the last, open-ended bucket. */
  max: number;
  lowest: number;
  highest: number;
}

export interface PriceScale {
  min: number;
  /** The top of the control. A handle here means "no upper limit". */
  max: number;
  step: number;
  /**
   * What a top-of-scale maximum is written as. The URL has always carried
   * both bounds together, so an open top still needs a number: with a chart
   * it is the dearest real price, so nothing above the last bar is dropped.
   */
  ceiling: number;
}

/** The staged price. `hi` null is "no upper limit". */
export interface StagedPrice {
  lo: number;
  hi: number | null;
}

const FALLBACK_STEP = 50;

/**
 * The span of the control. With the chart it runs from 0 to one bucket past
 * the chart's last step (the bars and the handles share one axis). Without it,
 * the page's own bounds, exactly as the old slider did.
 */
export function priceScale(bounds: { min: number; max: number }, histogram?: PriceHistogram | null): PriceScale {
  if (!histogram || histogram.buckets.length === 0 || histogram.bucketSize <= 0) {
    return { min: bounds.min, max: bounds.max, step: FALLBACK_STEP, ceiling: bounds.max };
  }
  const top = histogram.bucketSize * histogram.buckets.length;
  return { min: 0, max: top, step: histogram.bucketSize, ceiling: Math.max(top, Math.ceil(histogram.highest)) };
}

/** Where the handles sit for a staged price, clamped to the scale. */
export function shownRange(staged: StagedPrice, scale: PriceScale): { min: number; max: number } {
  const min = Math.min(Math.max(staged.lo, scale.min), scale.max);
  const max = staged.hi == null ? scale.max : Math.max(Math.min(staged.hi, scale.max), min);
  return { min, max };
}

/**
 * The bounds a staged price sends, or undefined for "any price". Same shape as
 * before this sheet: both `min_price` and `max_price`, or neither.
 */
export function priceBounds(staged: StagedPrice, scale: PriceScale): { min: number; max: number } | undefined {
  const { min, max } = shownRange(staged, scale);
  const atTop = staged.hi == null || staged.hi >= scale.max;
  if (min <= scale.min && atTop) return undefined;
  return { min, max: atTop ? scale.ceiling : max };
}

/** Is bucket `i` inside the kept range? Any overlap counts. */
export function bucketKept(i: number, histogram: PriceHistogram, range: { min: number; max: number }, scale: PriceScale): boolean {
  const from = i * histogram.bucketSize;
  const last = i === histogram.buckets.length - 1;
  const to = last ? Infinity : from + histogram.bucketSize;
  const top = range.max >= scale.max ? Infinity : range.max;
  return to > range.min && from <= top;
}

/** How many places sit at each price. Kept bars navy, the rest hairline. Decorative: the numbers are in the control below. */
export function PriceHistogramBars({
  histogram,
  range,
  scale,
}: {
  histogram: PriceHistogram;
  range: { min: number; max: number };
  scale: PriceScale;
}) {
  const peak = Math.max(1, ...histogram.buckets);
  return (
    <div className="flex h-16 items-end gap-[3px] px-[14px]" aria-hidden="true" data-testid="price-histogram">
      {histogram.buckets.map((n, i) => {
        const kept = bucketKept(i, histogram, range, scale);
        return (
          <span
            key={i}
            data-kept={kept ? "true" : "false"}
            className="min-w-0 flex-1 rounded-t-[3px] transition-colors duration-150"
            style={{
              // An empty bucket keeps a 2px stub, so the axis reads as
              // continuous rather than as missing bars.
              height: n === 0 ? 2 : `${Math.max(8, (n / peak) * 100)}%`,
              background: kept ? PW.navy : PW.innerHairline,
            }}
          />
        );
      })}
    </div>
  );
}

function snap(v: number, scale: PriceScale) {
  const stepped = Math.round((v - scale.min) / scale.step) * scale.step + scale.min;
  return Math.min(scale.max, Math.max(scale.min, stepped));
}

/** The white control with one navy hairline (owner: controls never wear the band). */
const CONTROL = {
  background: PW.controlFill,
  boxShadow: `0 0 0 1px ${PW.controlEdge}`,
  color: PW.navy,
} as const;

/**
 * Two custom handles on one track, plus a minimum and a maximum box with
 * minus / plus buttons. Arrow keys move a handle one step, Page keys four,
 * Home / End to the ends. In a right-to-left page the track runs from the
 * right, and so do the arrow keys.
 */
export function PriceRangeControl({
  scale,
  value,
  onChange,
}: {
  scale: PriceScale;
  value: StagedPrice;
  onChange: (next: StagedPrice) => void;
}) {
  const { t } = useTranslation();
  const display = useDisplayPrice();
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef<"lo" | "hi" | null>(null);
  const shown = shownRange(value, scale);
  const span = scale.max - scale.min || 1;
  const pct = (v: number) => ((v - scale.min) / span) * 100;

  const label = (v: number, top: boolean) => (top ? t("filter.price_plus", { price: display(v) }) : display(v));
  const loText = label(shown.min, false);
  const hiText = label(shown.max, shown.max >= scale.max);

  const setLo = useCallback(
    (v: number) => {
      const next = Math.min(snap(v, scale), shown.max - scale.step);
      onChange({ lo: Math.max(scale.min, next), hi: value.hi });
    },
    [scale, shown.max, value.hi, onChange],
  );
  const setHi = useCallback(
    (v: number) => {
      const next = Math.max(snap(v, scale), shown.min + scale.step);
      const clamped = Math.min(scale.max, next);
      onChange({ lo: value.lo, hi: clamped >= scale.max ? null : clamped });
    },
    [scale, shown.min, value.lo, onChange],
  );

  const isRtl = () => !!trackRef.current && getComputedStyle(trackRef.current).direction === "rtl";

  const valueAt = (clientX: number) => {
    const rect = trackRef.current!.getBoundingClientRect();
    const f = rect.width ? (isRtl() ? rect.right - clientX : clientX - rect.left) / rect.width : 0;
    return scale.min + Math.min(1, Math.max(0, f)) * span;
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!trackRef.current) return;
    const v = valueAt(e.clientX);
    // The nearer handle takes the drag; a tie goes to whichever can move.
    const which = Math.abs(v - shown.min) < Math.abs(v - shown.max) || (v < shown.min) ? "lo" : "hi";
    dragging.current = which;
    trackRef.current.setPointerCapture?.(e.pointerId);
    (which === "lo" ? setLo : setHi)(v);
    const thumb = trackRef.current.querySelector<HTMLElement>(`[data-thumb="${which}"]`);
    thumb?.focus({ preventScroll: true });
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    (dragging.current === "lo" ? setLo : setHi)(valueAt(e.clientX));
  };
  const endDrag = () => {
    dragging.current = null;
  };

  const onKey = (which: "lo" | "hi") => (e: KeyboardEvent<HTMLElement>) => {
    const current = which === "lo" ? shown.min : shown.max;
    const set = which === "lo" ? setLo : setHi;
    const forward = isRtl() ? "ArrowLeft" : "ArrowRight";
    const back = isRtl() ? "ArrowRight" : "ArrowLeft";
    let next: number | undefined;
    if (e.key === forward || e.key === "ArrowUp") next = current + scale.step;
    else if (e.key === back || e.key === "ArrowDown") next = current - scale.step;
    else if (e.key === "PageUp") next = current + scale.step * 4;
    else if (e.key === "PageDown") next = current - scale.step * 4;
    else if (e.key === "Home") next = scale.min;
    else if (e.key === "End") next = scale.max;
    if (next === undefined) return;
    e.preventDefault();
    set(next);
  };

  const thumb = (which: "lo" | "hi") => {
    const v = which === "lo" ? shown.min : shown.max;
    return (
      <span
        key={which}
        role="slider"
        tabIndex={0}
        data-thumb={which}
        aria-label={which === "lo" ? t("filter.price_min") : t("filter.price_max")}
        aria-valuemin={scale.min}
        aria-valuemax={scale.max}
        aria-valuenow={v}
        aria-valuetext={which === "lo" ? loText : hiText}
        onKeyDown={onKey(which)}
        className="absolute top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[#110E39] focus-visible:ring-offset-2"
        // Centred on its value in either direction: half its width back along the inline axis.
        style={{ insetInlineStart: `${pct(v)}%`, marginInlineStart: -16, zIndex: which === "hi" && v <= scale.min ? 1 : undefined }}
      >
        <span
          aria-hidden="true"
          className="block h-7 w-7 rounded-full"
          style={{ ...CONTROL, boxShadow: `0 0 0 1px ${PW.controlEdge}, 0 4px 12px -4px rgba(17,14,57,0.35)` }}
        />
      </span>
    );
  };

  return (
    <div>
      {/* The track sits flush under the bars: both span the same inset box. */}
      <div
        ref={trackRef}
        className="relative mx-[14px] h-9 touch-none select-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <span aria-hidden="true" className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full" style={{ background: PW.innerHairline }} />
        <span
          aria-hidden="true"
          className="absolute top-1/2 h-[3px] -translate-y-1/2 rounded-full"
          style={{ insetInlineStart: `${pct(shown.min)}%`, width: `${pct(shown.max) - pct(shown.min)}%`, background: PW.navy }}
        />
        {thumb("lo")}
        {thumb("hi")}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <Stepper
          caption={t("filter.price_min")}
          value={loText}
          lowerLabel={t("filter.price_min_lower")}
          raiseLabel={t("filter.price_min_raise")}
          canLower={shown.min > scale.min}
          canRaise={shown.min + scale.step < shown.max}
          onLower={() => setLo(shown.min - scale.step)}
          onRaise={() => setLo(shown.min + scale.step)}
        />
        <Stepper
          caption={t("filter.price_max")}
          value={hiText}
          lowerLabel={t("filter.price_max_lower")}
          raiseLabel={t("filter.price_max_raise")}
          canLower={shown.max - scale.step > shown.min}
          canRaise={shown.max < scale.max}
          onLower={() => setHi(shown.max - scale.step)}
          onRaise={() => setHi(shown.max + scale.step)}
        />
      </div>
    </div>
  );
}

function Stepper({
  caption,
  value,
  lowerLabel,
  raiseLabel,
  canLower,
  canRaise,
  onLower,
  onRaise,
}: {
  caption: string;
  value: string;
  lowerLabel: string;
  raiseLabel: string;
  canLower: boolean;
  canRaise: boolean;
  onLower: () => void;
  onRaise: () => void;
}) {
  const btn =
    "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full leading-none transition hover:bg-[rgba(17,14,57,0.06)] disabled:cursor-default disabled:opacity-35 disabled:hover:bg-transparent";
  return (
    <div className="flex min-w-0 items-center gap-1 rounded-[14px] px-1.5 py-1.5" style={CONTROL}>
      <button type="button" aria-label={lowerLabel} disabled={!canLower} onClick={onLower} className={btn} style={{ color: PW.navy }}>
        <Icon name="ri-subtract-line" className="text-[18px]" aria-hidden="true" />
      </button>
      <span className="flex min-w-0 flex-1 flex-col items-center leading-tight">
        <span className="text-[11.5px]" style={{ color: PW.muted }}>
          {caption}
        </span>
        {/* Prices are Latin digits in every locale; the label keeps the currency beside them. */}
        <span className="text-center text-[14px] font-semibold">
          {value}
        </span>
      </span>
      <button type="button" aria-label={raiseLabel} disabled={!canRaise} onClick={onRaise} className={btn} style={{ color: PW.navy }}>
        <Icon name="ri-add-line" className="text-[18px]" aria-hidden="true" />
      </button>
    </div>
  );
}
